import { useEffect, useMemo, useState, useCallback } from "react";
import BrandShell from "../components/BrandShell";
import DocumentUploader from "../components/DocumentUploader";
import { useAuth, apiCall } from "../auth/AuthContext";
import { track } from "../lib/analytics";
import {
  TrendingUp, Building2, Wallet, Briefcase, Layers, Banknote, ArrowRight,
  CheckCircle2, Circle, Loader2, Sparkles, Upload, Calendar, Zap,
} from "lucide-react";

const BOOKING_URL = "https://api.leadconnectorhq.com/widget/bookings/jc3-strategy-call";

const TABS = [
  { key: "credit", label: "Financial Profile Development", Icon: TrendingUp, source: "credit",
    columns: [
      ["Starting Score EQ", "Start EQ"], ["Current Score EQ", "Now EQ"],
      ["Score Gain", "Gain"], ["Dispute Round", "Review Round"],
      ["Items Removed", "Items Addressed"], ["Credit Status", "Status"], ["Last Updated", "Updated"],
    ] },
  { key: "business", label: "Business Capital Strategy", Icon: Briefcase, source: "business_funding",
    columns: [
      ["Lender Name", "Lender"], ["Funding Type", "Type"], ["Amount Applied", "Applied"],
      ["Amount Approved", "Approved"], ["Status", "Status"], ["Application Date", "Date"],
    ] },
  { key: "llc", label: "Business Foundation Setup", Icon: Building2, source: "llc",
    columns: [
      ["Business Name", "Business"], ["Entity Type", "Type"], ["State Filed", "State"],
      ["Date Filed", "Filed"], ["EIN Status", "EIN"], ["Formation Stage", "Stage"],
    ] },
  { key: "tradelines", label: "Account History Positioning", Icon: Layers, source: "tradelines",
    columns: [
      ["Tradeline Name", "Tradeline"], ["Credit Limit", "Limit"], ["Reporting Date", "Reports"],
      ["Days Until Reporting", "Days"], ["Bureaus Reporting", "Bureaus"], ["Reported", "Reported?"],
    ] },
  { key: "grants", label: "Grant Readiness & Research", Icon: Banknote, source: "grants",
    columns: [
      ["Grant Name", "Grant"], ["Grantor", "Grantor"], ["Amount Applied For", "Applied"],
      ["Application Deadline", "Deadline"], ["Days Until Deadline", "Days"], ["Status", "Status"],
    ] },
  { key: "personal", label: "Personal Capital Strategy", Icon: Wallet, source: "personal_funding",
    columns: [
      ["Lender Name", "Lender"], ["Product Type", "Product"], ["Amount Applied", "Applied"],
      ["Amount Approved", "Approved"], ["Status", "Status"], ["Application Date", "Date"],
    ] },
];

const fmt = (v) => {
  if (v == null || v === "") return "—";
  if (typeof v === "object") {
    if ("specialValue" in v) return "—";
    if (Array.isArray(v)) return v.join(", ") || "—";
    if ("name" in v) return v.name;
    if ("label" in v) return v.label;
    return "—";
  }
  if (typeof v === "number") return v.toLocaleString();
  if (typeof v === "boolean") return v ? "✓" : "—";
  return String(v);
};
const fmtMoney = (v) => {
  if (v && typeof v === "object" && "specialValue" in v) return "—";
  return typeof v === "number" ? `$${Math.round(v).toLocaleString()}` : v ?? "—";
};

const MONEY_KEYS = new Set(["Credit Limit", "Amount Applied", "Amount Approved", "Amount Applied For", "Amount Awarded"]);

export default function ClientDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("credit");
  const [services, setServices] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [s, ob] = await Promise.all([apiCall("/api/client/me/services"), apiCall("/api/client/me/onboarding")]);
      setServices(s);
      setOnboarding(ob);
    } catch {
      setServices({ credit: [], llc: [], personal_funding: [], business_funding: [], tradelines: [], grants: [], documents: [], client: null });
      setOnboarding({ steps: { account_created: true, strategy_call_booked: false, onboarding_docs_uploaded: false, service_activated: false }, complete: 1, total: 4 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const active = TABS.find((t) => t.key === tab) || TABS[0];
  const rows = services?.[active.source] || [];
  const client = useMemo(() => services?.client || {}, [services]);

  const progressPct = useMemo(() => onboarding ? Math.round((onboarding.complete / onboarding.total) * 100) : 25, [onboarding]);

  const checklistItems = [
    { key: "account_created", label: "Account Created", desc: "You're in. Welcome." },
    { key: "strategy_call_booked", label: "Strategy Call Booked", desc: "Schedule a 30-min consult with CEO Jace." },
    { key: "onboarding_docs_uploaded", label: "Onboarding Docs Uploaded", desc: "Submit the docs Jace requests." },
    { key: "service_activated", label: "Service Activated", desc: "Your selected services go live." },
  ];

  // ---- Recommended Next Step (derived from onboarding + service_package) ----
  const nextStep = useMemo(() => {
    const steps = onboarding?.steps || {};
    const pkg = (client["Service Package"] || user?.service_package || "Custom").trim();
    if (!steps.strategy_call_booked) {
      return {
        Icon: Calendar,
        kicker: "Your Next Step",
        title: "Book your 30-minute strategy call with CEO Jace.",
        body: "This is where we map your personalized roadmap based on your goals and your profile. It's the fastest path to results.",
        cta: { label: "Book Strategy Call", href: BOOKING_URL, external: true, tid: "next-step-book" },
      };
    }
    if (!steps.onboarding_docs_uploaded) {
      return {
        Icon: Upload,
        kicker: "Your Next Step",
        title: "Upload your onboarding documents.",
        body: "Add your ID, recent credit reports, and any documents Jace requested. Use the Documents section below — you can upload up to 5 files at a time.",
        cta: { label: "Go to Documents", href: "#client-documents", external: false, tid: "next-step-upload" },
      };
    }
    if (!steps.service_activated) {
      return {
        Icon: Zap,
        kicker: "You're in Position",
        title: "Your service is being activated.",
        body: "Jace is finalizing your strategy. Expect a follow-up within 24 hours with your activation plan. Keep an eye on your inbox.",
        cta: { label: "Email CEO Jace", href: "mailto:jace@jc3wealthlegacy.com", external: true, tid: "next-step-dm" },
      };
    }
    return {
      Icon: Sparkles,
      kicker: "You're All Set",
      title: `Your ${pkg} plan is active and running.`,
      body: "Use the service tabs below to track your progress. Need a check-in or want to add a service? Book a session any time.",
      cta: { label: "Book a Check-In", href: BOOKING_URL, external: true, tid: "next-step-checkin" },
    };
  }, [onboarding, client, user]);

  return (
    <BrandShell dashboardChrome>
      <section data-testid="client-dashboard" className="bg-jc-black text-jc-white min-h-[calc(100vh-5rem)]">
        <div className="relative">
          <div className="absolute inset-0 jc-grid-bg opacity-40 pointer-events-none" />
          <div className="relative mx-auto max-w-7xl px-6 md:px-10 py-12">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-jc-gold mb-3">◆ Client Portal</p>
            <h1 className="font-display text-jc-white" style={{ fontSize: "clamp(2.4rem, 5vw, 4rem)", lineHeight: 0.92 }}>
              WELCOME, <span className="text-jc-gold">{(user?.first_name || "CLIENT").toUpperCase()}.</span>
            </h1>
            <p className="mt-4 font-body text-lg text-jc-muted max-w-2xl">
              Your private command center. Track services, see updates, upload documents — all in one place.
            </p>

            {/* ====== Recommended Next Step ====== */}
            {!loading && nextStep ? (
              <div
                data-testid="next-step-callout"
                className="mt-10 border border-jc-gold bg-gradient-to-br from-jc-gold/10 via-jc-black/30 to-jc-black/10 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6"
              >
                <div className="flex-shrink-0 h-14 w-14 rounded-full bg-jc-gold/15 border border-jc-gold flex items-center justify-center text-jc-gold">
                  <nextStep.Icon size={24} strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold mb-2">◆ {nextStep.kicker}</p>
                  <h3 className="font-display text-xl md:text-2xl text-jc-white leading-tight" data-testid="next-step-title">
                    {nextStep.title}
                  </h3>
                  <p className="mt-2 font-body text-sm text-jc-muted leading-relaxed max-w-2xl">{nextStep.body}</p>
                </div>
                {nextStep.cta.external ? (
                  <a
                    href={nextStep.cta.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={nextStep.cta.tid}
                    className="jc-btn jc-btn-primary flex-shrink-0"
                  >
                    {nextStep.cta.label} <ArrowRight size={14} strokeWidth={2.5} />
                  </a>
                ) : (
                  <a
                    href={nextStep.cta.href}
                    data-testid={nextStep.cta.tid}
                    className="jc-btn jc-btn-primary flex-shrink-0"
                  >
                    {nextStep.cta.label} <ArrowRight size={14} strokeWidth={2.5} />
                  </a>
                )}
              </div>
            ) : null}

            {/* AI Credit Analyzer entry card (Sprint 2 / 7B) */}
            <div data-testid="ai-credit-analyzer-card" className="mt-8 border border-jc-gold/35 bg-jc-navy/40 p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-5">
              <div className="flex-shrink-0 h-14 w-14 rounded-full bg-jc-gold/15 border border-jc-gold flex items-center justify-center text-jc-gold">
                <Sparkles size={24} strokeWidth={1.75} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold mb-2">◆ Your Services</p>
                <h3 className="font-display text-xl md:text-2xl text-jc-white leading-tight">Every step. Every milestone. One view.</h3>
                <p className="mt-2 font-body text-sm text-jc-muted leading-relaxed max-w-2xl">Your services, your progress, and your next steps in financial profile development — all in one place.</p>
              </div>
              <a href="/dashboard/client/products" data-testid="products-tools-link" className="jc-btn jc-btn-primary flex-shrink-0">
                My Services <ArrowRight size={14} strokeWidth={2.5} />
              </a>
              <a href="/dashboard/client/journey" data-testid="client-journey-link" className="jc-btn jc-btn-primary flex-shrink-0">
                Open Journey <ArrowRight size={14} strokeWidth={2.5} />
              </a>
            </div>

            {/* Dispute Tracker + Funding Match + Analyzer tiles */}
            <div className="mt-6 grid md:grid-cols-3 gap-5">
              <a href="/dashboard/client/profile-insights" data-testid="ai-credit-analyzer-link" className="block border border-jc-gold/25 bg-jc-navy/40 p-5 hover:border-jc-gold/60 transition-colors group">
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold mb-1">◆ AI Profile Insights</p>
                <h4 className="font-display text-lg text-jc-white">Upload your documents</h4>
                <p className="text-xs text-jc-muted mt-1">Understand what shapes your financial profile with an AI-powered read of your records. Educational analysis only. You stay in control of every decision.</p>
                <span className="mt-3 inline-flex items-center text-xs font-mono uppercase tracking-[0.22em] text-jc-gold group-hover:translate-x-1 transition-transform">
                  Open insights <ArrowRight size={12} className="ml-1.5"/>
                </span>
              </a>
              <a href="/dashboard/client/records-review" data-testid="dispute-tracker-tile" className="block border border-jc-gold/25 bg-jc-navy/40 p-5 hover:border-jc-gold/60 transition-colors group">
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold mb-1">◆ Financial Records Review</p>
                <h4 className="font-display text-lg text-jc-white">Review for accuracy</h4>
                <p className="text-xs text-jc-muted mt-1">Review your financial records for accuracy and learn how to address inaccurate items directly with the source yourself. We provide education and tools, not filing services.</p>
                <span className="mt-3 inline-flex items-center text-xs font-mono uppercase tracking-[0.22em] text-jc-gold group-hover:translate-x-1 transition-transform">
                  View tracker <ArrowRight size={12} className="ml-1.5"/>
                </span>
              </a>
              <a href="/dashboard/client/funding-match" data-testid="funding-match-tile" className="block border border-jc-gold/25 bg-jc-navy/40 p-5 hover:border-jc-gold/60 transition-colors group">
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold mb-1">◆ AI Funding Readiness</p>
                <h4 className="font-display text-lg text-jc-white">See where you stand</h4>
                <p className="text-xs text-jc-muted mt-1">See where you stand against common lender criteria and which capital options fit your profile. Matched by AI, decided by you.</p>
                <span className="mt-3 inline-flex items-center text-xs font-mono uppercase tracking-[0.22em] text-jc-gold group-hover:translate-x-1 transition-transform">
                  Run match <ArrowRight size={12} className="ml-1.5"/>
                </span>
              </a>
            </div>

            {/* Onboarding Checklist */}
            <div data-testid="onboarding-checklist" className="mt-10 border border-jc-gold/25 bg-jc-black/40 backdrop-blur-sm p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold mb-2">◆ Onboarding</p>
                  <h2 className="font-display text-2xl md:text-3xl text-jc-white">YOUR JOURNEY</h2>
                </div>
                <div className="flex items-center gap-4">
                  <div className="font-display text-3xl md:text-4xl text-jc-gold leading-none" data-testid="onboarding-progress">{progressPct}%</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-muted">{onboarding?.complete || 1} of {onboarding?.total || 4} complete</div>
                </div>
              </div>
              <div className="h-1 bg-jc-navy mb-7 overflow-hidden"><div className="h-full bg-jc-gold transition-all duration-700" style={{ width: `${progressPct}%` }} /></div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {checklistItems.map((it) => {
                  const done = onboarding?.steps?.[it.key];
                  return (
                    <div key={it.key} data-testid={`step-${it.key}`} className={`p-5 border ${done ? "border-jc-gold bg-jc-gold/5" : "border-jc-gold/20 bg-jc-black/30"} transition-colors`}>
                      <div className="flex items-center justify-between mb-3">
                        {done ? <CheckCircle2 size={22} className="text-jc-gold" /> : <Circle size={22} className="text-jc-muted" />}
                        <span className={`font-mono text-[9px] uppercase tracking-[0.25em] ${done ? "text-jc-gold" : "text-jc-muted"}`}>{done ? "Done" : "Pending"}</span>
                      </div>
                      <div className={`font-display text-base tracking-wide ${done ? "text-jc-white" : "text-jc-white/90"}`}>{it.label.toUpperCase()}</div>
                      <div className="mt-1 font-body text-xs text-jc-muted">{it.desc}</div>
                      {it.key === "strategy_call_booked" && !done && (
                        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.25em] text-jc-gold hover:text-jc-gold-light">
                          Book now <ArrowRight size={10} />
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Quick stats */}
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-jc-gold/15">
              {[
                ["Status", client["Client Status"] || "Onboarding"],
                ["Package", client["Service Package"] || user?.service_package || "—"],
                ["Days as Client", client["Days as Client"] != null ? String(client["Days as Client"]) : "—"],
                ["Referral Code Used", client["Referral Code Used"] || "—"],
              ].map(([k, v]) => (
                <div key={k} className="bg-jc-black p-6">
                  <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold">{k}</div>
                  <div className="mt-3 font-display text-2xl md:text-3xl text-jc-white truncate">{v}</div>
                </div>
              ))}
            </div>

            {/* Tabs */}
            <div className="mt-12">
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-jc-gold mb-4">My Services</div>
              <div className="flex flex-wrap gap-2 mb-6">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => { setTab(t.key); track("service-tab-view", { tab: t.key }); }}
                    data-testid={`tab-${t.key}`}
                    className={`px-4 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border transition-colors ${
                      tab === t.key ? "bg-jc-gold text-jc-black border-jc-gold" : "border-jc-gold/30 text-jc-muted hover:text-jc-white hover:border-jc-gold/70"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2"><t.Icon size={12} />{t.label}</span>
                  </button>
                ))}
              </div>

              <div data-testid={`tab-panel-${active.key}`} className="border border-jc-gold/25 bg-jc-black/40 backdrop-blur-sm">
                <div className="px-6 py-5 border-b border-jc-gold/15 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 border border-jc-gold/40 flex items-center justify-center"><active.Icon size={16} className="text-jc-gold" /></div>
                    <h2 className="font-display text-xl md:text-2xl text-jc-white tracking-wide">{active.label.toUpperCase()}</h2>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-jc-muted">{rows.length} {rows.length === 1 ? "record" : "records"}</span>
                </div>

                {loading ? (
                  <div className="p-12 text-center"><Loader2 size={20} className="inline-block animate-spin text-jc-gold" /></div>
                ) : rows.length === 0 ? (
                  <div className="p-10 text-center">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-jc-muted">No records yet for this service.</p>
                    <p className="mt-3 font-body text-sm text-jc-muted">After your strategy call, CEO Jace will activate your services and entries will appear here.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left">
                          {active.columns.map(([_, lbl]) => (
                            <th key={lbl} className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-jc-gold whitespace-nowrap border-b border-jc-gold/15">{lbl}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={r.id || i} className="hover:bg-jc-navy/30 transition-colors">
                            {active.columns.map(([k, lbl]) => (
                              <td key={lbl} className="px-5 py-4 border-b border-jc-gold/10 font-body text-jc-white">
                                {MONEY_KEYS.has(k) ? fmtMoney(r[k]) : fmt(r[k])}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Documents */}
            <div id="client-documents" className="mt-10 scroll-mt-24">
              <DocumentUploader existing={services?.documents || []} onUploaded={(body) => {
                try { track("document-upload", { count: body?.summary?.ok || 0, status: body?.status }); } catch (e) { console.warn("analytics track failed", e); }
                fetchAll();
              }} />
            </div>
          </div>
        </div>
      </section>
    </BrandShell>
  );
}
